import { destroy, get, post, put } from "@/application/api";
import type { SortItem } from "@/application/composables/usePaginatedResourceList";
import toSortParam from "@/application/utils/toSortParam";
import type {
  PeriodResource,
  PeriodSelectResource,
} from "@/modules/periods/types";

interface PeriodApiResource {
  id: string;
  name: string;
  starts_at: string;
  ends_at: string;
  required_volunteer_hours: number;
  active: boolean;
  can_delete: boolean;
}

const toResource = (raw: PeriodApiResource): PeriodResource => ({
  id: raw.id,
  name: raw.name,
  startsAt: raw.starts_at,
  endsAt: raw.ends_at,
  requiredVolunteerHours: raw.required_volunteer_hours,
  active: raw.active,
  canDelete: raw.can_delete,
});

export const getPeriods = (
  page: number,
  size: number,
  search: string,
  sortBy: SortItem[] = [],
) =>
  get<{ content: PeriodApiResource[]; page: { total_elements: number } }>(
    "/v1/club-admin/volunteer/periods",
    {
      params: {
        page,
        size,
        name: search || undefined,
        sort: toSortParam(sortBy),
      },
    },
  ).then((res) => ({
    content: res.data.content.map(toResource),
    totalElements: res.data.page.total_elements,
  }));

export const getPeriod = (id: string) =>
  get<PeriodApiResource>(`/v1/club-admin/volunteer/periods/${id}`).then((res) =>
    toResource(res.data),
  );

export const getPeriodsForSelect = () =>
  get<PeriodSelectResource[]>("/v1/club-admin/volunteer/periods/select").then(
    (res) => res.data,
  );

interface PeriodPayload {
  name: string;
  startsAt: string;
  endsAt: string;
  requiredVolunteerHours: number;
}

export const createPeriod = (payload: PeriodPayload) =>
  post<PeriodApiResource>("/v1/club-admin/volunteer/periods", {
    name: payload.name,
    starts_at: payload.startsAt,
    ends_at: payload.endsAt,
    required_volunteer_hours: payload.requiredVolunteerHours,
  }).then((res) => toResource(res.data));

export const updatePeriod = (
  id: string,
  payload: PeriodPayload & { active: boolean },
) =>
  put<PeriodApiResource>(`/v1/club-admin/volunteer/periods/${id}`, {
    name: payload.name,
    starts_at: payload.startsAt,
    ends_at: payload.endsAt,
    required_volunteer_hours: payload.requiredVolunteerHours,
    active: payload.active,
  }).then((res) => toResource(res.data));

export const deletePeriod = (id: string) =>
  destroy(`/v1/club-admin/volunteer/periods/${id}`);

export const activatePeriod = (period: PeriodResource) =>
  updatePeriod(period.id, {
    name: period.name,
    startsAt: period.startsAt,
    endsAt: period.endsAt,
    requiredVolunteerHours: period.requiredVolunteerHours,
    active: true,
  });

export const exportPeriodProgress = (id: string) =>
  get<Blob>(`/v1/club-admin/volunteer/periods/${id}/export`, {
    responseType: "blob",
    headers: { Accept: "text/csv" },
  }).then((res) => res.data);
